import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './LandingPage';
import SearchPage from './SearchPage';
import WorkspacePage from './WorkspacePage';
import LibraryPage from './LibraryPage';
import RequireAuth from './components/RequireAuth';
import { auth } from './firebase/config';
import GridBackground from './components/GridBackground';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import SubscriptionsPage from './SubscriptionsPage';
import { CompleteProfile } from './components/CompleteProfile';
import { AuthProvider } from './contexts/AuthContext';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import PricingPage from './pages/PricingPage';
import { Analytics } from "@vercel/analytics/react"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function ErrorFallback({ error }) {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{error.message}</pre>
    </div>
  )
}

function App() {
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(() => {
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (authLoading) {
    return (
      <div className="relative w-full h-screen bg-black overflow-hidden">
        <GridBackground />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite] text-blue-500"></div>
            <div className="mt-4 text-white/80 text-sm">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthProvider>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/refund" element={<RefundPolicy />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route element={<RequireAuth />}>
                <Route path="/search" element={<SearchPage />} />
                <Route path="/workspace" element={<WorkspacePage />} />
                <Route path="/library" element={<LibraryPage />} />
                <Route path="/subscription" element={<SubscriptionsPage />} />
              </Route>
              <Route path="/complete-profile" element={<CompleteProfile />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </QueryClientProvider>
      </ErrorBoundary>
    </AuthProvider>
  );
}

export default App;